import React from "react";
import { Badge, Box, Flex, Icon, Image, Stack, Text, useColorModeValue } from "@chakra-ui/react";
import { MdPerson, MdPublic, MdImage } from "react-icons/md";

/**
 * Single info row with an icon and text label for a service property.
 * @param {object} props
 * @param {React.ElementType} props.icon - react-icons component
 * @param {string} props.text - Display text
 * @param {boolean} [props.bold=false] - Whether to render text in bold style
 * @returns {React.ReactElement}
 */
function ServiceInfoRow({ icon, text, bold }) {
    const iconColor = useColorModeValue("secondaryGray.500", "secondaryGray.400");
    const textColor = useColorModeValue("secondaryGray.900", "white");
    return (
        <Flex align="center" gap="8px">
            <Icon as={icon} w="16px" h="16px" color={iconColor} />
            <Text fontSize="sm" fontWeight={bold ? "600" : "400"} color={textColor}>{text}</Text>
        </Flex>
    );
}

/**
 * Row of small gallery thumbnail images.
 * @param {object} props
 * @param {Array<string>} props.urls - Array of image URLs to display
 * @returns {React.ReactElement}
 */
function GalleryThumbnails({ urls }) {
    return (
        <Flex gap="8px" flexWrap="wrap" mt="4px">
            {urls.map((url, idx) => (
                <Image key={idx} src={url} alt={`Gallery ${idx + 1}`} boxSize="50px"
                    objectFit="cover" borderRadius="8px" />
            ))}
        </Flex>
    );
}

/**
 * Single service card showing ID, artist name, country, and gallery thumbnails.
 * @param {object} props
 * @param {object} props.service - Service data with id, artist_name, country, galleries_count, galleries_sample
 * @returns {React.ReactElement}
 */
function ServiceCard({ service }) {
    const bg = useColorModeValue("secondaryGray.100", "whiteAlpha.100");
    const hasGallery = service.galleries_sample && service.galleries_sample.length > 0;
    return (
        <Box p="14px" bg={bg} borderRadius="12px">
            <Stack spacing="10px">
                <Badge colorScheme="blue" borderRadius="6px" px="8px" w="fit-content" fontWeight="700">#{service.id}</Badge>
                <ServiceInfoRow icon={MdPerson} text={service.artist_name || "No name"} bold />
                <ServiceInfoRow icon={MdPublic} text={service.country || "N/A"} />
                <ServiceInfoRow icon={MdImage} text={`${service.galleries_count || 0} image(s)`} />
                {hasGallery && <GalleryThumbnails urls={service.galleries_sample} />}
            </Stack>
        </Box>
    );
}

/**
 * Displays a list of sample service entries from the ElasticSearch index.
 * @param {object} props
 * @param {Array} props.services - Array of sample service objects
 * @returns {React.ReactElement}
 */
function ElasticServiceSampleList({ services }) {
    const titleColor = useColorModeValue("secondaryGray.900", "white");
    return (
        <Box>
            <Text fontSize="md" fontWeight="600" color={titleColor} mb="12px">Service samples</Text>
            <Stack spacing="12px">
                {services.map((service, index) => (
                    <ServiceCard key={index} service={service} />
                ))}
            </Stack>
        </Box>
    );
}

export default ElasticServiceSampleList;
